import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import { ActiveListingResource } from "api/resource/active-listing";
import { ActiveListingModifyResource, UpdateActiveListingModifyResource } from "api/resource/active-listing-modify";
import { loginReset } from "redux/slice/authentication";

/**
 * Active listings report (listingseditor.cfm / agentprop.cfm) — MAX-3882. Read-only; the full set is
 * fetched once and the table pages/sorts client-side. The fetch branches on admin vs agent, mirroring
 * the two CF templates behind the single /listings/active route.
 *
 * The native "Modify Listing" editor (listingsmodify.cfm) — MAX-3899 — lives in the same page family, so
 * it shares this slice (as off-market pairs its list + editor in one slice). It edits one AllDesc override
 * row at a time keyed by (listingNumber, boardId), so the edit target is held as a single `modifying`
 * object rather than a byId map.
 */
export interface ActiveListingsState {
	loading?: boolean;
	loaded?: boolean;
	error?: string | null;
	listings: ActiveListingResource[];
	modifyLoading?: boolean;
	modifyError?: string | null;
	modifying: ActiveListingModifyResource | null;
}

const initialState: ActiveListingsState = {
	loading: false,
	loaded: false,
	listings: [],
	modifying: null,
};

export const fetchActiveListings = createAsyncThunk(
	"activeListings/fetch",
	async (payload: { admin: boolean; agentId: number }, thunkAPI) => {
		thunkAPI.dispatch(activeListingsLoading());
		// Admin → all featured listings for the client; agent → that agent's listings.
		// MAX-4256: activeFeaturedForReport asks ihf-root for this report's narrower result set —
		// active MLS listings only, no supplementals, matching CF's Active report. It used to be
		// implicit in how the shared /listings.json branches behaved, which regressed the mobile
		// app's AI listing picker (it needs pending, sold and supplementals). The report now states
		// what it wants instead of the endpoint assuming every caller wants the same thing.
		const params = payload.admin
			? { featuredOnlyYn: true, activeFeaturedForReport: true, context: "RESULT" }
			: { agentId: payload.agentId, activeFeaturedForReport: true, context: "RESULT" };
		const result = api.getActiveListings(params);
		result
			.then((listings) => thunkAPI.dispatch(activeListingsLoaded(listings)))
			.catch((error) => thunkAPI.dispatch(activeListingsFailed(error)));
		return result;
	}
);

export const fetchActiveListingModify = createAsyncThunk(
	"activeListings/fetchModify",
	async (payload: { listingNumber: string; boardId: number }, thunkAPI) => {
		thunkAPI.dispatch(activeListingModifyLoading());
		const result = api.getActiveListingModify(payload);
		result
			.then((listing) => thunkAPI.dispatch(activeListingModifyLoaded(listing)))
			.catch((error) => thunkAPI.dispatch(activeListingModifyFailed(error)));
		return result;
	}
);

export const updateActiveListingModify = createAsyncThunk(
	"activeListings/updateModify",
	async (
		payload: { listingNumber: string; boardId: number; body: UpdateActiveListingModifyResource },
		thunkAPI
	) => {
		const result = api.updateActiveListingModify(
			{ listingNumber: payload.listingNumber, boardId: payload.boardId },
			payload.body
		);
		result
			.then((listing) => thunkAPI.dispatch(activeListingModifyLoaded(listing)))
			.catch((error) => thunkAPI.dispatch(activeListingModifyFailed(error)));
		return result;
	}
);

export const activeListingsSlice = createSlice({
	name: "activeListings",
	initialState,
	reducers: {
		activeListingsLoading: (state) => {
			state.loading = true;
		},
		activeListingsLoaded: (state, action: PayloadAction<ActiveListingResource[]>) => {
			state.loading = false;
			state.loaded = true;
			state.error = null;
			state.listings = action.payload;
		},
		activeListingsFailed: (state, action: PayloadAction<any>) => {
			state.loading = false;
			state.error = action.payload && action.payload.message;
		},
		activeListingModifyLoading: (state) => {
			state.modifyLoading = true;
		},
		activeListingModifyLoaded: (state, action: PayloadAction<ActiveListingModifyResource>) => {
			state.modifyLoading = false;
			state.modifyError = null;
			state.modifying = action.payload;
		},
		activeListingModifyFailed: (state, action: PayloadAction<any>) => {
			state.modifyLoading = false;
			state.modifyError = action.payload && action.payload.message;
		},
		activeListingModifyCleared: (state) => {
			state.modifying = null;
			state.modifyError = null;
			state.modifyLoading = false;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, () => {
			return initialState;
		});
	},
});

export const {
	activeListingsLoading,
	activeListingsLoaded,
	activeListingsFailed,
	activeListingModifyLoading,
	activeListingModifyLoaded,
	activeListingModifyFailed,
	activeListingModifyCleared,
} = activeListingsSlice.actions;
